import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { APP_LANGUAGES, applyAppLanguage, currentAppLanguage } from "../../utils/appLanguages";
import defaultAppLogo from "../../assets/smarttt-wordmark.png";
import { LegalFooter } from "../../components/legal/LegalFooter";
import { SCFORH_INFO_URL } from "../../constants/scforhClubHealth";

export function scrollToId(id) {
  if (!id) return;
  setTimeout(() => {
    const el = document.getElementById(id);
    if (!el) return;
    const navInner = document.querySelector(".mh-nav__inner");
    const headerHeight = navInner ? navInner.offsetHeight : 72;
    const extraOffset = window.innerWidth <= 960 ? 12 : 20;
    const offset = headerHeight + extraOffset;
    const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: Math.max(0, offsetPosition),
      behavior: "smooth",
    });
  }, 60);
}

export function MarketingHashLink({ hashId, className, children, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <a
      href={`/#${hashId}`}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        if (onNavigate) onNavigate();
        if (location.pathname === "/") {
          scrollToId(hashId);
          window.history.replaceState(null, "", `/#${hashId}`);
        } else {
          navigate(`/#${hashId}`);
        }
      }}
    >
      {children}
    </a>
  );
}

export function MarketingChrome({ children }) {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lang = currentAppLanguage(i18n);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const changeLanguage = (e) => {
    applyAppLanguage(i18n, e.target.value);
  };

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <div className="mh">
      <a className="mh-skip" href="#mh-main">
        {t("mhSkipToContent", "Skip to content")}
      </a>
      <header className={`mh-nav${scrolled ? " mh-nav--solid" : ""}`}>
        <div className="mh-nav__inner">
          <Link to="/" className="mh-nav__brand" onClick={closeMenu}>
            <img src={defaultAppLogo} alt="SmartTT Club" />
          </Link>
          <nav className="mh-nav__links" aria-label={t("mhNavAria", "Page sections")}>
            <MarketingHashLink hashId="product">{t("mhNavProduct", "Product")}</MarketingHashLink>
            <MarketingHashLink hashId="pingflo">{t("mhNavPingflo", "Pingflo & Agents")}</MarketingHashLink>
            <MarketingHashLink hashId="flows">{t("mhNavFlows", "Flows")}</MarketingHashLink>
            <MarketingHashLink hashId="method">{t("mhNavMethod", "Method & Health")}</MarketingHashLink>
            <MarketingHashLink hashId="pilot">{t("mhNavPilot", "Pilot")}</MarketingHashLink>
            <MarketingHashLink hashId="trust">{t("mhNavTrust", "Trust")}</MarketingHashLink>
          </nav>
          <div className="mh-nav__actions">
            <label className="mh-lang mh-lang--bar">
              <span className="mh-sr">{t("mhLangLabel", "Language")}</span>
              <select value={lang} onChange={changeLanguage} aria-label={t("mhLangLabel", "Language")}>
                {APP_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.nativeName}
                  </option>
                ))}
              </select>
            </label>
            <Link to="/login" className="mh-btn mh-btn--ghost mh-nav__auth">
              {t("mhSignIn", "Sign in")}
            </Link>
            <Link to="/register" className="mh-btn mh-btn--primary mh-nav__auth">
              {t("mhSignUp", "Sign up")}
            </Link>
            <MarketingHashLink hashId="pilot" className="mh-btn mh-btn--ghost mh-nav__pilot">
              {t("mhRequestPilot", "Request a pilot")}
            </MarketingHashLink>
            <button
              type="button"
              className="mh-nav__burger"
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              <span className={`mh-nav__burger-icon${mobileMenuOpen ? " is-open" : ""}`} />
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="mh-nav__mobile-drawer" role="dialog" aria-modal="true">
            <nav className="mh-nav__mobile-links">
              <MarketingHashLink hashId="product" onNavigate={closeMenu}>
                {t("mhNavProduct", "Product")}
              </MarketingHashLink>
              <MarketingHashLink hashId="pingflo" onNavigate={closeMenu}>
                {t("mhNavPingflo", "Pingflo & Agents")}
              </MarketingHashLink>
              <MarketingHashLink hashId="flows" onNavigate={closeMenu}>
                {t("mhNavFlows", "The Four Flows")}
              </MarketingHashLink>
              <MarketingHashLink hashId="method" onNavigate={closeMenu}>
                {t("mhNavMethod", "SCforH & Health")}
              </MarketingHashLink>
              <MarketingHashLink hashId="pilot" onNavigate={closeMenu}>
                {t("mhNavPilot", "Cohort Zero Pilot")}
              </MarketingHashLink>
              <MarketingHashLink hashId="trust" onNavigate={closeMenu}>
                {t("mhNavTrust", "Trust & Privacy")}
              </MarketingHashLink>
            </nav>
            <label className="mh-lang mh-lang--drawer">
              <span className="mh-sr">{t("mhLangLabel", "Language")}</span>
              <select value={lang} onChange={changeLanguage} aria-label={t("mhLangLabel", "Language")}>
                {APP_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.nativeName}
                  </option>
                ))}
              </select>
            </label>
            <div className="mh-nav__mobile-actions">
              <Link to="/login" className="mh-btn mh-btn--ghost" onClick={closeMenu}>
                {t("mhSignIn", "Sign in")}
              </Link>
              <Link to="/register" className="mh-btn mh-btn--primary" onClick={closeMenu}>
                {t("mhSignUp", "Sign up")}
              </Link>
              <MarketingHashLink hashId="pilot" className="mh-btn mh-btn--ghost" onNavigate={closeMenu}>
                {t("mhRequestPilot", "Request a pilot")}
              </MarketingHashLink>
            </div>
          </div>
        )}
      </header>
      {children}
      <footer className="mh-footer">
        <div className="mh-wrap mh-footer__grid">
          <div>
            <img src={defaultAppLogo} alt="" className="mh-footer__logo" />
            <p className="mh-footer__tag">{t("mhFooterTagline")}</p>
          </div>
          <div>
            <p className="mh-footer__meta">{t("mhFooterCopy")}</p>
            <p className="mh-footer__meta">
              <a href={SCFORH_INFO_URL} target="_blank" rel="noopener noreferrer">
                scforh.info — Sports Clubs for Health
              </a>
            </p>
            <LegalFooter className="mh-legal" />
          </div>
        </div>
      </footer>
    </div>
  );
}
